import React, { useEffect, useRef } from "react";
import type { SponsoredAdSlot } from "../../../Redux/ecommersProduct/types";
import { useAppSelector } from "../../../Redux/hooks";

// Ad_Service's own NestJS app mounts its global prefix as REACT_APP_ADVERTISEMENTS_API_PERFIX
// (e.g. /api/v1/advertisement) — every route already lives under that path, so callers
// must not also append "/api/v1" themselves.
const AD_SERVICE_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_ADVERTISEMENTS_API_PERFIX,
]
  .filter(Boolean)
  .join("");

const SESSION_KEY = "salar_ad_session_id";
const FALLBACK_IMG = "/assets/img/salarlogo.png";

// A few older campaigns still have the seller's raw login email stored as
// advertiserName (backend bug, fixed at the source, but existing data is unaffected) —
// never render a value that looks like an email on a public storefront page.
const isEmailLike = (s?: string): boolean => !!s && /\S+@\S+\.\S+/.test(s);

const getSessionId = (): string => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

type Props = {
  slot: SponsoredAdSlot;
};

const renderStars = (rating: number) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    let iconClass = "bi bi-star text-black-50 opacity-5";
    if (i <= Math.floor(rating)) {
      iconClass = "bi bi-star-fill text-success";
    } else if (i - rating <= 0.5) {
      iconClass = "bi bi-star-half text-success";
    }
    stars.push(
      <i key={i} className={`${iconClass} text-xxs ${i > 1 ? "ms-01" : ""}`}></i>,
    );
  }
  return stars;
};

// Same card markup/classes as ProductCard.tsx's default variant, so sponsored and organic
// results read as one visual system. Backend hydration (EcomCatalogService.getProductCardsByIds)
// now attaches the same price/rating/delivery/color fields an organic card gets — sections
// below still degrade gracefully (hidden, not faked) if a field is missing for a given slot.
const SponsoredProductCard: React.FC<Props> = ({ slot }) => {
  const ref = useRef<HTMLDivElement>(null);
  const fired = useRef(false);
  // Attribution (linking a later order back to this click/impression) is keyed by
  // userId when logged in, falling back to sessionId — Ad_Service's budget/attribution
  // service already reads dto.userId, this was just never sent.
  const userId = useAppSelector((state) => state.customerAuth.user?._id);

  useEffect(() => {
    if (!ref.current || fired.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !fired.current) {
          fired.current = true;
          fetch(`${AD_SERVICE_URL}/ads/impression`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              impressionId: slot.impressionId ?? "",
              trackingToken: slot.trackingToken,
              sessionId: getSessionId(),
              userId,
            }),
          }).catch(() => null);
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [slot.trackingToken, userId]);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    // The PDP route is /DetailPage/:id and expects a catalog product id (or SSIN).
    // Prefer productDetailId (resolved server-side from the SellerListing's
    // catalogProductId); productId is the SellerListing._id which the PDP can't load.
    const detailUrl = slot.productDetailId
      ? `/DetailPage/${slot.productDetailId}`
      : slot.productId
      ? `/DetailPage/${slot.productId}`
      : null;
    // Fire the click for tracking/charging, but navigate to the PDP ourselves.
    try {
      await fetch(`${AD_SERVICE_URL}/ads/click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          trackingToken: slot.trackingToken,
          sessionId: getSessionId(),
          userId,
        }),
      });
    } catch {}
    if (detailUrl) {
      window.location.href = detailUrl;
    }
  };

  const image =
    slot.creative?.imageUrl || slot.creative?.logoUrl || FALLBACK_IMG;
  const safeAdvertiserName = isEmailLike(slot.advertiserName) ? undefined : slot.advertiserName;
  const title = slot.productName || safeAdvertiserName || "Product";
  const brandName = slot.brand || slot.creative?.brandName || safeAdvertiserName || "Salar Store";
  const description = slot.creative?.headline || title;

  const finalPrice = slot.finalPrice ?? 0;
  const mrp = slot.mrp ?? 0;
  const hasPrice = slot.finalPrice !== undefined;

  const avgRating = slot.rating?.average ?? 0;
  const totalReviews = slot.rating?.count ?? 0;
  const formattedTotalReviews =
    totalReviews >= 1000 ? `${(totalReviews / 1000).toFixed(1)}k` : `${totalReviews}`;

  const isFreeDelivery = slot.freeDelivery === true;
  const deliveryTime = slot.fastDelivery ? "Today" : slot.nextDayDelivery ? "Tomorrow" : "Tomorrow";
  const hasDelivery = slot.freeDelivery !== undefined || slot.fastDelivery !== undefined || slot.nextDayDelivery !== undefined;

  return (
    <div className="col-6 col-md-3 mb-3">
      <div
        ref={ref}
        className="cat-pro-width card border-radius-0 shadow-none mb-lg-0 w-100"
        style={{ cursor: "pointer" }}
        onClick={handleClick}
      >
        <div className="card-header p-0 position-relative border-radius-lg z-index-2 bg-gray-100">
          <span className="badge badge-xs bg-gray-400 border-radius-2xl text-white ms-2">
            Sponsored
          </span>
          <div className="d-block blur-shadow-image">
            <img
              src={image}
              alt={title}
              className="img-fluid-cat-pro p-2"
              style={{ height: "16.5rem", objectFit: "contain" }}
              loading="lazy"
              onError={(e) => {
                e.currentTarget.src = FALLBACK_IMG;
              }}
            />
          </div>
        </div>
        <div className="p-2 text-start lh-0">
          <div className="px-2">
            <div className="text-start lh-0">
              <div>
                <b className="font-weight-bold text-sm">{brandName}</b>
              </div>
              <div className="lh-sm">
                <b className="fw-normal text-xxs text-black">
                  <div className="">
                    {description.length < 80 ? description : description.slice(0, 80) + "..."}
                  </div>
                </b>
              </div>
              {slot.rating && (
                <div className="mt-1 mb-2">
                  <span>
                    <b className="text-black fw-normal text-xxs">
                      {avgRating.toFixed(1)} {renderStars(avgRating)}
                      <b className="text-black-50 fw-normal text-xxs mx-1">
                        ({formattedTotalReviews})
                      </b>
                    </b>
                  </span>
                </div>
              )}
            </div>
            {hasPrice && (
              <div className="mb-2 mt-2 px-1">
                <div className="lh-1">
                  <b className="font-bold fs-5 text-black my-auto">
                    <sup className="fw-normal">₹</sup>
                    {finalPrice.toLocaleString("en-IN")}{" "}
                  </b>
                  {mrp > finalPrice && (
                    <span>
                      <b className="fw-normal text-black text-xxs">MRP: </b>{" "}
                      <b className="fw-normal text-black text-xxs text-decoration-line-through">
                        {mrp.toLocaleString("en-IN")}
                      </b>
                    </span>
                  )}
                </div>
                {hasDelivery && (
                  <div className="lh-base">
                    <b className="text-black text-xxs fw-normal">
                      <i className="ri-truck-line me-1 text-xs"></i>
                      {isFreeDelivery ? "Free Delivery " : "Delivery "}
                      <b className="text-black text-xs">{deliveryTime}</b>
                    </b>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="text-center">
          <button
            className="btn btn-md w-95 fw-bolder text-black btn-outline-dark"
            onClick={handleClick}
          >
            {slot.creative?.ctaText || "Shop Now"}
            <i className="bi bi-chevron-right ms-1 text-black text-sm"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SponsoredProductCard;
